'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { AddToCartModal } from '@/components/marketing/add-to-cart-modal'
import { getDemoProduct, type DemoPost } from '@/lib/copy/demo-products'
import type { WidgetConfig } from '@/lib/widget/embed'

// A real client-rendered widget: a grid/carousel/masonry/etc of sample
// UGC tiles that honours columns/mobileColumns/spacing/view/accent. The
// CSS variables drive the responsive column count and gap so the preview
// reacts to every slider in real time.
//
// Shared between the flagship /tools/widget-builder and the homepage
// builder band, so it lives in its own module: importing it never pulls
// the full builder (stepper, provisioning, Turnstile) into a bundle.
export function SamplePreview({
  config,
  posts,
  reducedMotion,
  showHotspots = true,
}: {
  config: WidgetConfig
  posts: DemoPost[]
  reducedMotion: boolean
  showHotspots?: boolean
}) {
  const accent = config.accent
  const gap = `${config.spacing}px`
  // The currently-open product card. Set by clicking a pin; clearing it
  // closes the modal. Demonstrates the shoppable in-grid → in-modal flow
  // the real widget renders (founder ask: "show how products look on it
  // and on modal"). Pure sample data, no backend.
  const [active, setActive] = React.useState<{ productId: string; post: DemoPost } | null>(null)
  const activeProduct = active ? getDemoProduct(active.productId) : null
  const openHotspot = React.useCallback(
    (post: DemoPost, productId: string) => setActive({ productId, post }),
    [],
  )

  // Show each sample image once (no concat) so the preview never reads as
  // the same photo tiled over and over — dedupe by image just in case.
  const tiles = React.useMemo(() => {
    const seen = new Set<string>()
    return posts.filter((p) => (seen.has(p.image) ? false : (seen.add(p.image), true)))
  }, [posts])

  const modal = (
    <AddToCartModal product={activeProduct} post={active?.post} onClose={() => setActive(null)} />
  )

  // stories + reelCarousel are intrinsically horizontal; everything else
  // is a column grid (masonry/collage approximated with span tricks).
  if (config.view === 'stories') {
    return (
      <>
        <div className="overflow-hidden rounded-xl border bg-background p-3">
          <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {tiles.slice(0, 8).map((p, i) => (
              <figure key={i} className="shrink-0 text-center">
                <div
                  className="relative h-16 w-16 overflow-hidden rounded-full"
                  style={{ boxShadow: `0 0 0 2px ${accent}` }}
                >
                  <TileImage post={p} reducedMotion={reducedMotion} sizes="64px" />
                </div>
                <figcaption className="mt-1 max-w-[64px] truncate text-[10px] text-muted-foreground">
                  {p.user}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        {modal}
      </>
    )
  }

  if (config.view === 'carousel' || config.view === 'reelCarousel') {
    const reel = config.view === 'reelCarousel'
    return (
      <>
        <div className="overflow-hidden rounded-xl border bg-background p-3">
          <div className="flex overflow-x-auto pb-1" style={{ gap, scrollbarWidth: 'none' }}>
            {tiles.slice(0, 8).map((p, i) => (
              <div
                key={i}
                className={cn(
                  'relative shrink-0 overflow-hidden rounded-lg',
                  reel ? 'aspect-[9/16] w-28' : 'aspect-square w-40',
                )}
              >
                <TileImage post={p} reducedMotion={reducedMotion} sizes="160px" />
                {showHotspots && (
                  <Pin
                    post={p}
                    accent={accent}
                    reducedMotion={reducedMotion}
                    onOpen={openHotspot}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        {modal}
      </>
    )
  }

  // grid / masonry / collage / wall2 — CSS columns with per-breakpoint count.
  const masonryish = config.view === 'masonry' || config.view === 'collage'
  return (
    <>
      <div
        className="idk-preview-grid rounded-xl border bg-background p-3"
        style={
          {
            '--idk-cols': config.columns,
            '--idk-mcols': config.mobileColumns,
            '--idk-gap': gap,
          } as React.CSSProperties
        }
      >
        <style>{`
          .idk-preview-grid > .idk-grid {
            display: grid;
            gap: var(--idk-gap);
            grid-template-columns: repeat(var(--idk-mcols), minmax(0, 1fr));
          }
          @media (min-width: 640px) {
            .idk-preview-grid > .idk-grid {
              grid-template-columns: repeat(var(--idk-cols), minmax(0, 1fr));
            }
          }
        `}</style>
        <div className="idk-grid">
          {tiles.map((p, i) => {
            // collage: first tile features (spans 2x2); masonry: alternate row spans.
            const feature = config.view === 'collage' && i === 0
            const ratio = masonryish
              ? feature
                ? 'aspect-square'
                : i % 3 === 0
                  ? 'aspect-[3/4]'
                  : 'aspect-square'
              : 'aspect-square'
            return (
              <div
                key={i}
                className={cn(
                  'relative overflow-hidden rounded-lg',
                  ratio,
                  feature && 'col-span-2 row-span-2',
                )}
              >
                <TileImage
                  post={p}
                  reducedMotion={reducedMotion}
                  sizes="(min-width: 640px) 18vw, 45vw"
                />
                {showHotspots && (
                  <Pin
                    post={p}
                    accent={accent}
                    reducedMotion={reducedMotion}
                    onOpen={openHotspot}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
      {modal}
    </>
  )
}

function TileImage({
  post,
  reducedMotion,
  sizes,
}: {
  post: DemoPost
  reducedMotion: boolean
  sizes: string
}) {
  return (
    <ImgWithFallback
      src={post.image}
      alt={post.caption}
      fill
      sizes={sizes}
      className={cn(
        'object-cover',
        !reducedMotion && 'transition-transform duration-500 ease-out hover:scale-[1.04]',
      )}
    />
  )
}

// A single shoppable hotspot pin tinted with the chosen accent. Now
// interactive: clicking it opens the sample product modal, mirroring
// the real widget's "tap to shop" flow. A soft accent-tinted ping ring
// draws the eye (skipped under reduced motion).
function Pin({
  post,
  accent,
  reducedMotion,
  onOpen,
}: {
  post: DemoPost
  accent: string
  reducedMotion: boolean
  onOpen: (post: DemoPost, productId: string) => void
}) {
  const hotspot = post.hotspots[0]
  if (!hotspot) return null
  const product = getDemoProduct(hotspot.productId)
  return (
    <button
      type="button"
      onClick={() => onOpen(post, hotspot.productId)}
      aria-label={product ? `Shop ${product.name}` : 'Shop tagged product'}
      className="absolute z-10 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[10px] font-bold leading-none text-foreground shadow-md ring-2 ring-white/70 transition-transform duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:hover:scale-110"
      style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%`, backgroundColor: accent }}
      title={product?.name}
    >
      +
      {!reducedMotion && (
        <span
          className="absolute inset-0 -z-10 animate-ping rounded-full opacity-60"
          style={{ backgroundColor: accent }}
          aria-hidden
        />
      )}
    </button>
  )
}
